import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const TIMEOUT_MS = 30 * 60 * 1000;  // 30 min
const WARNING_MS =  2 * 60 * 1000;  // warn at 2 min left

export default function SessionTimeout() {
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown]     = useState(120);
  const { logout }  = useAuth();
  const navigate    = useNavigate();
  const logoutTimer = useRef(null);
  const warnTimer   = useRef(null);
  const countTimer  = useRef(null);

  const clearAll = () => {
    clearTimeout(logoutTimer.current);
    clearTimeout(warnTimer.current);
    clearInterval(countTimer.current);
  };

  const doLogout = useCallback(() => {
    clearAll();
    logout();
    navigate('/login');
  }, [logout, navigate]);

  const resetTimer = useCallback(() => {
    clearAll();
    setShowWarning(false);
    setCountdown(120);

    warnTimer.current = setTimeout(() => {
      setShowWarning(true);
      setCountdown(120);
      countTimer.current = setInterval(() => {
        setCountdown(c => {
          if (c <= 1) { clearInterval(countTimer.current); return 0; }
          return c - 1;
        });
      }, 1000);
    }, TIMEOUT_MS - WARNING_MS);

    logoutTimer.current = setTimeout(doLogout, TIMEOUT_MS);
  }, [doLogout]);

  useEffect(() => {
    resetTimer();
    const events = ['mousemove', 'keydown', 'click', 'touchstart', 'scroll'];
    events.forEach(e => window.addEventListener(e, resetTimer, { passive: true }));
    return () => {
      clearAll();
      events.forEach(e => window.removeEventListener(e, resetTimer));
    };
  }, [resetTimer]);

  if (!showWarning) return null;

  const mins = Math.floor(countdown / 60);
  const secs = countdown % 60;
  const pct  = (countdown / 120) * 100;

  return (
    <div style={s.overlay}>
      <div style={s.modal}>
        {/* Countdown ring */}
        <div style={s.ringWrap}>
          <svg width="80" height="80" viewBox="0 0 80 80">
            <circle cx="40" cy="40" r="34" fill="none" stroke="#e2e8f0" strokeWidth="6" />
            <circle cx="40" cy="40" r="34" fill="none"
              stroke={pct > 50 ? '#3cb74e' : pct > 20 ? '#f59e0b' : '#ef4444'}
              strokeWidth="6" strokeLinecap="round"
              strokeDasharray={`${2 * Math.PI * 34 * pct / 100} ${2 * Math.PI * 34}`}
              transform="rotate(-90 40 40)"
              style={{ transition: 'stroke-dasharray .9s linear, stroke .5s' }}
            />
            <text x="40" y="38" textAnchor="middle" fontSize="13" fontWeight="700" fill="#111111">
              {mins > 0 ? `${mins}m` : `${secs}s`}
            </text>
            <text x="40" y="52" textAnchor="middle" fontSize="9" fill="#64748b">left</text>
          </svg>
        </div>

        <h3 style={s.title}>Session Expiring Soon</h3>
        <p style={s.msg}>
          You'll be logged out in{' '}
          <strong style={{ color: countdown <= 30 ? '#ef4444' : '#f59e0b' }}>
            {mins > 0 ? `${mins}m ${secs}s` : `${secs}s`}
          </strong>{' '}
          due to inactivity.
        </p>

        <div style={s.btnRow}>
          <button style={s.stayBtn} onClick={resetTimer}>✅ Stay Logged In</button>
          <button style={s.logoutBtn} onClick={doLogout}>🚪 Logout Now</button>
        </div>
      </div>
    </div>
  );
}

const s = {
  overlay:  { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, backdropFilter: 'blur(3px)' },
  modal:    { background: '#ffffff', borderRadius: 18, padding: '32px 28px', textAlign: 'center', maxWidth: 340, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' },
  ringWrap: { marginBottom: 14 },
  title:    { color: '#111111', fontSize: 17, fontWeight: 700, margin: '0 0 10px' },
  msg:      { color: '#64748b', fontSize: 14, marginBottom: 22, lineHeight: 1.5 },
  btnRow:   { display: 'flex', gap: 10 },
  stayBtn:  { flex: 1, padding: '11px', background: '#3cb74e', color: '#ffffff', border: 'none', borderRadius: 9, fontWeight: 700, fontSize: 14, cursor: 'pointer' },
  logoutBtn:{ flex: 1, padding: '11px', background: '#f1f5f9', color: '#64748b', border: '1.5px solid #e2e8f0', borderRadius: 9, fontWeight: 600, fontSize: 14, cursor: 'pointer' },
};
